import classNames from 'classnames';
import React, { ReactNode, useMemo, useRef } from 'react';
import { InfoCircleIcon, SearchIcon } from '../icons';
import { formatValidationError, getTextInputExample } from '../utils/validation-error.ts';
import { TooltipNew } from './TooltipNew.tsx';

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string | ReactNode;
  labelButton?: React.ReactNode;
  placeholder?: string;
  error?: string;
  withFile?: boolean;
  setFile?: (file: File) => void | Promise<void>;
  attachedFileName?: string;
  textarea?: boolean;
  withBorder?: boolean;
  limit?: number;
  rounded?: 'full' | 'right' | 'left';
  buttons?: React.ReactNode[];
  hintLabel?: string;
  onLimitExceeded?: () => void;
  hideErrorMessage?: boolean;
}

export const TextInput = ({
  label,
  error,
  name,
  labelButton,
  className,
  placeholder,
  type,
  withFile = false,
  setFile,
  attachedFileName,
  textarea = false,
  limit,
  value,
  onChange,
  buttons,
  hintLabel,
  onLimitExceeded,
  hideErrorMessage = false,
  id,
  withBorder = false,
  ...rest
}: TextInputProps) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleAttachFile = () => {
    if (!fileRef.current) return;
    fileRef.current.click();
  };

  const handleChangeAttachFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFile && setFile(file);
  };

  const TagName = useMemo(
    () =>
      textarea === true
        ? ('textarea' as keyof JSX.IntrinsicElements)
        : ('input' as keyof JSX.IntrinsicElements),
    [textarea],
  );

  return (
    <>
      <div
        className={classNames('relative', {
          // 'flex flex-col items-end': limit && limit > 0,
          'w-full': !className?.includes('w-'),
        })}
      >
        {(label || labelButton) && (
          <label htmlFor={id || name} className="flex flex-row justify-between">
            <span className="flex gap-1 items-center relative">
              <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase">
                {label}
              </span>
              {hintLabel && (
                <TooltipNew text={hintLabel}>
                  <img alt="info" src={InfoCircleIcon} className="min-w-4 min-h-4 h-4 w-4" />
                </TooltipNew>
              )}
            </span>
            <span className="flex gap-4 items-center">
              <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] font-medium uppercase">
                {labelButton}
              </span>
              {limit && limit > 0 && (
                <span className="text-xs leading-5 text-[#6B7280]">
                  {value?.toString().length ?? 0} / {limit}
                </span>
              )}
            </span>
          </label>
        )}
        <TagName
          id={id || name}
          name={name}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          type={type}
          placeholder={placeholder}
          className={classNames(
            'block focus:outline-none',
            'transition-colors',
            'ease-linear',
            'bg-white disabled:bg-white',
            'placeholder:text-gray-400 placeholder:opacity-100 disabled:placeholder:text-gray-400 disabled:placeholder:opacity-100',
            {
              'min-h-[48px]': !className?.includes('min-h-'),
              'py-[14px]': !className?.includes('py-'),
              'min-w-[216px]': !className?.includes('min-w-'),
              'text-gray-900 text-sm disabled:text-opacity-48': !className?.includes('text-'),
              'w-full': !className?.includes('w-'),
              'border-b': withBorder === false,
              'border rounded-sm pl-2': withBorder,
              'border-error-red border-opacity-100 focus:border-opacity-100': error,
              'border-gray-900 border-opacity-16 disabled:border-opacity-8': !error,
              'focus:border-opacity-100': !error && !className?.includes('focus:border-'),
              'pl-10 pr-2': type === 'search',
            },
            className,
          )}
          value={value}
          onChange={(e: any) => {
            const text = e.target.value;
            if (
              !limit ||
              limit < 1 ||
              (text.toString().length ?? 0) <= limit ||
              (text.toString().length ?? 0) < (value?.toString().length ?? 0)
            ) {
              onChange && onChange(e);
            } else {
              onLimitExceeded?.();
            }
          }}
          {...rest}
          autoComplete="off"
        />
        {error && !hideErrorMessage && (
          <p className="mt-1 text-sm text-[12px] text-error-red">
            {formatValidationError(error, getTextInputExample({ name, placeholder, type }))}
          </p>
        )}
        {buttons &&
          buttons.map((button, index) => <React.Fragment key={index}>{button}</React.Fragment>)}
        {type === 'search' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <img src={SearchIcon} alt="search" className="h-4 w-4 min-w-[16px] min-h-[16px]" />
          </div>
        )}
        {/* {type === 'password' && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center px-2"
            onClick={toggleShowPassword}
          >
            {showPassword ? (
              <p className="text-sm text-gray-500 opacity-80">Hide</p>
            ) : (
              <img
                src={'eyePath'}
                alt="show password"
                title="Show Password"
                className="h-4 w-4 min-w-[16px] min-h-[16px]"
              />
            )}
          </button>
        )} */}
        {withFile === true && (
          <>
            <button
              type="button"
              className={classNames('absolute inset-y-0 left-0 flex items-center h-[40px]', {
                'px-2.5 pt-2.5': textarea === true,
                'px-2': !textarea,
              })}
              onClick={handleAttachFile}
            >
              <img
                src={'AttachIcon'}
                alt="Attach file"
                title="Attach"
                className="h-5 w-5 min-w-[20px] min-h-[20px]"
              />
            </button>
            <input ref={fileRef} type="file" onChange={handleChangeAttachFile} className="hidden" />
          </>
        )}
      </div>
      {attachedFileName && (
        <p className="mt-2 text-sm text-gray-500 flex items-center">
          <img
            src={'AttachIcon'}
            alt="Attached File"
            className="w-4 h-4 min-w-[16px] min-h-[16px] mr-4"
          />{' '}
          {attachedFileName}
        </p>
      )}
    </>
  );
};
